// ThemeCustomizationModal.js
import React, { useState, useRef, useEffect } from "react";
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  TextField,
  Grid,
  Typography,
  Box,
  Tabs,
  Tab,
  Card,
  CardContent,
  IconButton,
  Switch,
  FormControlLabel,
  Chip,
  makeStyles,
  Collapse,
  LinearProgress,
} from "@material-ui/core";
import {
  Close as CloseIcon,
  Palette as PaletteIcon,
  GetApp as DownloadIcon,
  Publish as UploadIcon,
  Refresh as RefreshIcon,
  Visibility as PreviewIcon,
  Shuffle as ShuffleIcon,
  Save as SaveIcon,
  ColorLens as ColorLensIcon,
  Brightness4 as DarkModeIcon,
  Brightness7 as LightModeIcon,
  Settings as SettingsIcon,
} from "@material-ui/icons";
import { useTheme } from "../../context/themeContext";

const useStyles = makeStyles((theme) => ({
  dialog: {
    "& .MuiDialog-paper": {
      maxWidth: "900px",
      width: "100%",
      maxHeight: "95vh",
      borderRadius: "16px",
      overflow: "hidden",
    },
  },
  dialogTitle: ({ currentTheme }) => ({
    display: "flex",
    alignItems: "center",
    justifyContent: "space-between",
    padding: theme.spacing(3, 3, 2, 3),
    background: `linear-gradient(135deg, ${
      currentTheme?.primary || theme.palette.primary.main
    } 0%, ${currentTheme?.secondary || theme.palette.secondary.main} 100%)`,
    color: "white",
    position: "relative",
    overflow: "hidden",
    "&::before": {
      content: '""',
      position: "absolute",
      top: 0,
      left: 0,
      right: 0,
      bottom: 0,
      background: "rgba(255, 255, 255, 0.1)",
      backdropFilter: "blur(10px)",
    },
  }),
  titleContent: {
    position: "relative",
    zIndex: 1,
    display: "flex",
    alignItems: "center",
    gap: theme.spacing(1),
  },
  closeButton: {
    position: "relative",
    zIndex: 1,
    color: "white",
    backgroundColor: "rgba(255, 255, 255, 0.1)",
    "&:hover": {
      backgroundColor: "rgba(255, 255, 255, 0.2)",
    },
  },
  dialogContent: {
    padding: 0,
    overflow: "auto",
  },
  tabsContainer: {
    borderBottom: `1px solid ${theme.palette.divider}`,
    backgroundColor: theme.palette.background.paper,
  },
  tabPanel: {
    padding: theme.spacing(3),
    minHeight: "500px",
    maxHeight: "60vh",
    overflowY: "auto",
    "&::-webkit-scrollbar": {
      width: "6px",
    },
    "&::-webkit-scrollbar-track": {
      background: theme.palette.grey[100],
    },
    "&::-webkit-scrollbar-thumb": {
      background: theme.palette.grey[400],
      borderRadius: "3px",
    },
  },
  colorInput: {
    width: "100%",
    "& input[type='color']": {
      width: "100%",
      height: "50px",
      border: "none",
      borderRadius: "8px",
      cursor: "pointer",
      boxShadow: theme.shadows[2],
    },
  },
  presetCard: ({ currentTheme }) => ({
    cursor: "pointer",
    transition: "all 0.3s cubic-bezier(0.4, 0, 0.2, 1)",
    border: "2px solid transparent",
    borderRadius: "12px",
    overflow: "hidden",
    "&:hover": {
      transform: "translateY(-4px)",
      boxShadow: theme.shadows[8],
    },
  }),
  activePreset: ({ currentTheme }) => ({
    borderColor: currentTheme?.primary || theme.palette.primary.main,
    boxShadow: `0 0 0 2px ${
      currentTheme?.primary || theme.palette.primary.main
    }33`,
  }),
  presetPreview: {
    height: "80px",
    borderRadius: "8px",
    marginBottom: theme.spacing(1),
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    color: "white",
    fontWeight: "bold",
    fontSize: "0.9rem",
    textShadow: "0 2px 4px rgba(0,0,0,0.3)",
    position: "relative",
    overflow: "hidden",
    "&::before": {
      content: '""',
      position: "absolute",
      top: 0,
      left: 0,
      right: 0,
      bottom: 0,
      background: "rgba(0, 0, 0, 0.1)",
    },
  },
  gradientPreview: {
    height: "50px",
    borderRadius: "8px",
    border: "2px solid #ddd",
    marginTop: theme.spacing(1),
    position: "relative",
    overflow: "hidden",
  },
  sectionTitle: {
    marginBottom: theme.spacing(2),
    fontWeight: 600,
    display: "flex",
    alignItems: "center",
    gap: theme.spacing(1),
    color: theme.palette.text.primary,
  },
  propertyGroup: {
    marginBottom: theme.spacing(3),
    padding: theme.spacing(2.5),
    border: `1px solid ${theme.palette.divider}`,
    borderRadius: "12px",
    backgroundColor: theme.palette.background.paper,
    boxShadow: theme.shadows[1],
    transition: "all 0.2s ease",
    "&:hover": {
      boxShadow: theme.shadows[3],
    },
  },
  livePreview: ({ currentTheme }) => ({
    position: "sticky",
    top: theme.spacing(2),
    padding: theme.spacing(2.5),
    borderRadius: "12px",
    border: `2px solid ${currentTheme?.borderColor || theme.palette.divider}`,
    marginBottom: theme.spacing(3),
    boxShadow: theme.shadows[4],
    transition: "all 0.3s ease",
  }),
  hiddenFileInput: {
    display: "none",
  },
  actionButton: {
    borderRadius: "8px",
    padding: theme.spacing(1, 2),
    fontWeight: 600,
    textTransform: "none",
    minHeight: "40px",
  },
  primaryActionButton: ({ currentTheme }) => ({
    background: `linear-gradient(45deg, ${
      currentTheme?.primary || theme.palette.primary.main
    } 30%, ${currentTheme?.secondary || theme.palette.secondary.main} 90%)`,
    color: "white",
    "&:hover": {
      transform: "translateY(-2px)",
      boxShadow: `0 6px 20px ${
        currentTheme?.primary || theme.palette.primary.main
      }66`,
    },
  }),
  colorSwatch: {
    width: "24px",
    height: "24px",
    borderRadius: "50%",
    border: "2px solid white",
    boxShadow: theme.shadows[2],
    marginRight: theme.spacing(1),
  },
  progressContainer: {
    padding: theme.spacing(2, 3),
    borderBottom: `1px solid ${theme.palette.divider}`,
  },
  tabBadge: {
    marginLeft: theme.spacing(1),
    fontSize: "0.7rem",
    minWidth: "18px",
    height: "18px",
  },
  exportOptions: {
    display: "grid",
    gridTemplateColumns: "repeat(auto-fit, minmax(120px, 1fr))",
    gap: theme.spacing(1),
    marginTop: theme.spacing(2),
  },
}));

function TabPanel({ children, value, index, ...other }) {
  const classes = useStyles({ currentTheme: null });
  return (
    <div
      role="tabpanel"
      hidden={value !== index}
      id={`theme-tabpanel-${index}`}
      aria-labelledby={`theme-tab-${index}`}
      {...other}
    >
      {value === index && <Box className={classes.tabPanel}>{children}</Box>}
    </div>
  );
}

export default function ThemeCustomizationModal({ open, onClose }) {
  const {
    theme,
    updateThemeProperty,
    applyPreset,
    resetToDefault,
    generateGradient,
    themePresets,
  } = useTheme();

  const classes = useStyles({ currentTheme: theme });
  const [activeTab, setActiveTab] = useState(0);
  const [tempTheme, setTempTheme] = useState({ ...theme });
  const [gradientDirection, setGradientDirection] = useState("135deg");
  const [gradientColor1, setGradientColor1] = useState(theme.primary);
  const [gradientColor2, setGradientColor2] = useState(theme.secondary);
  const [showPreview, setShowPreview] = useState(true);
  const [customThemeName, setCustomThemeName] = useState(theme.name || "");

  useEffect(() => {
    setTempTheme({ ...theme });
    setGradientColor1(theme.primary);
    setGradientColor2(theme.secondary);
    setCustomThemeName(theme.name || "");
  }, [theme]);

  const handleTabChange = (event, newValue) => {
    setActiveTab(newValue);
  };

  const handlePropertyChange = (property, value) => {
    setTempTheme((prev) => ({ ...prev, [property]: value }));
    updateThemeProperty(property, value);
  };

  const handlePresetSelect = (presetName) => {
    applyPreset(presetName);
  };

  const handleGradientUpdate = () => {
    const newGradient = generateGradient(
      gradientColor1,
      gradientColor2,
      gradientDirection
    );
    handlePropertyChange("gradient", newGradient);
  };

  const ColorInput = ({ label, property, value, description }) => (
    <Grid item xs={12} sm={6} md={4}>
      <Box>
        <TextField
          label={label}
          type="color"
          value={value}
          onChange={(e) => handlePropertyChange(property, e.target.value)}
          className={classes.colorInput}
          variant="outlined"
          size="small"
        />
        {description && (
          <Typography
            variant="caption"
            color="textSecondary"
            display="block"
            mt={0.5}
          >
            {description}
          </Typography>
        )}
      </Box>
    </Grid>
  );

  const TextInput = ({
    label,
    property,
    value,
    description,
    multiline = false,
  }) => (
    <Grid item xs={12} sm={multiline ? 12 : 6}>
      <TextField
        label={label}
        value={value}
        onChange={(e) => handlePropertyChange(property, e.target.value)}
        variant="outlined"
        size="small"
        fullWidth
        multiline={multiline}
        rows={multiline ? 3 : 1}
        helperText={description}
      />
    </Grid>
  );

  const tabs = [
    {
      label: "Presets",
      icon: <PaletteIcon />,
      count: Object.keys(themePresets).length,
    },
    { label: "Colors", icon: <ColorLensIcon />, count: 12 },
    { label: "Layout", icon: <LightModeIcon />, count: 8 },
  ];

  return (
    <Dialog
      open={open}
      onClose={onClose}
      className={classes.dialog}
      maxWidth={false}
    >
      <DialogTitle>
        <Box className={classes.dialogTitle}>
          <Box className={classes.titleContent}>
            <Typography variant="h6" component="div">
              Theme Customization
            </Typography>
            <Chip
              size="small"
              label={theme.name || "Custom"}
              style={{
                backgroundColor: "rgba(255,255,255,0.2)",
                color: "white",
              }}
            />
          </Box>
          <IconButton
            onClick={onClose}
            size="small"
            className={classes.closeButton}
          >
            <CloseIcon />
          </IconButton>
        </Box>
      </DialogTitle>

      <DialogContent className={classes.dialogContent} dividers>
        <Collapse in={showPreview}>
          <Box p={3}>
            <Card
              className={classes.livePreview}
              style={{
                background: tempTheme.gradient,
                color: tempTheme.text,
              }}
            >
              <CardContent>
                <Box
                  display="flex"
                  justifyContent="space-between"
                  alignItems="center"
                  mb={2}
                >
                  <Typography variant="h6" gutterBottom>
                    Live Preview
                  </Typography>
                  <IconButton
                    size="small"
                    onClick={() => setShowPreview(!showPreview)}
                    style={{ color: tempTheme.text }}
                  >
                    <PreviewIcon />
                  </IconButton>
                </Box>

                <Box display="flex" gap={1} flexWrap="wrap" mb={2}>
                  <Button
                    variant="contained"
                    size="small"
                    style={{
                      backgroundColor: tempTheme.primary,
                      color: "white",
                    }}
                  >
                    Primary
                  </Button>
                  <Button
                    variant="contained"
                    size="small"
                    style={{
                      backgroundColor: tempTheme.secondary,
                      color: "white",
                    }}
                  >
                    Secondary
                  </Button>
                  <Button
                    variant="contained"
                    size="small"
                    style={{
                      backgroundColor: tempTheme.success,
                      color: "white",
                    }}
                  >
                    Success
                  </Button>
                  <Button
                    variant="contained"
                    size="small"
                    style={{
                      backgroundColor: tempTheme.warning,
                      color: "white",
                    }}
                  >
                    Warning
                  </Button>
                  <Button
                    variant="contained"
                    size="small"
                    style={{ backgroundColor: tempTheme.error, color: "white" }}
                  >
                    Error
                  </Button>
                </Box>

                <Box
                  p={2}
                  mb={2}
                  style={{
                    backgroundColor: tempTheme.cardBackground,
                    color: tempTheme.text,
                    border: `1px solid ${tempTheme.borderColor}`,
                    borderRadius: "8px",
                  }}
                >
                  <Typography variant="body2">
                    Sample card content showcasing current theme colors and
                    styling.
                  </Typography>
                  <Typography
                    variant="caption"
                    style={{ color: tempTheme.textSecondary }}
                  >
                    Secondary text example
                  </Typography>
                </Box>

                <Box display="flex" gap={1}>
                  {[
                    tempTheme.primary,
                    tempTheme.secondary,
                    tempTheme.success,
                    tempTheme.warning,
                    tempTheme.error,
                  ].map((color, index) => (
                    <div
                      key={index}
                      className={classes.colorSwatch}
                      style={{ backgroundColor: color }}
                    />
                  ))}
                </Box>
              </CardContent>
            </Card>
          </Box>
        </Collapse>

        {/* Tabs */}
        <Box className={classes.tabsContainer}>
          <Tabs
            value={activeTab}
            onChange={handleTabChange}
            indicatorColor="primary"
            textColor="primary"
            variant="scrollable"
            scrollButtons="auto"
          >
            {tabs.map((tab, index) => (
              <Tab
                key={index}
                label={
                  <Box display="flex" alignItems="center">
                    {tab.icon}
                    <Box ml={1}>{tab.label}</Box>
                    {tab.count > 0 && (
                      <Chip
                        size="small"
                        label={tab.count}
                        className={classes.tabBadge}
                        color="primary"
                      />
                    )}
                  </Box>
                }
              />
            ))}
          </Tabs>
        </Box>

        {/* Preset Tab */}
        <TabPanel value={activeTab} index={0}>
          <Typography className={classes.sectionTitle}>
            <PaletteIcon />
            Choose a Preset Theme
          </Typography>
          <Grid container spacing={3}>
            {Object.entries(themePresets).map(([key, preset]) => (
              <Grid item xs={12} sm={6} md={4} key={key}>
                <Card
                  className={`${classes.presetCard} ${
                    theme.name === preset.name ? classes.activePreset : ""
                  }`}
                  onClick={() => handlePresetSelect(key)}
                >
                  <CardContent>
                    <Box
                      className={classes.presetPreview}
                      style={{ background: preset.gradient }}
                    >
                      <Typography
                        variant="subtitle2"
                        style={{ position: "relative", zIndex: 1 }}
                      >
                        {preset.name}
                      </Typography>
                    </Box>
                    <Typography variant="caption" color="textSecondary">
                      {preset.mode} mode
                    </Typography>
                    <Box display="flex" mt={1} gap={0.5}>
                      {[
                        preset.primary,
                        preset.secondary,
                        preset.success,
                        preset.warning,
                      ].map((color, index) => (
                        <div
                          key={index}
                          style={{
                            width: "16px",
                            height: "16px",
                            borderRadius: "50%",
                            backgroundColor: color,
                            border: "1px solid rgba(255,255,255,0.3)",
                          }}
                        />
                      ))}
                    </Box>
                  </CardContent>
                </Card>
              </Grid>
            ))}
          </Grid>
        </TabPanel>

        {/* Colors Tab */}
        <TabPanel value={activeTab} index={1}>
          <Box className={classes.propertyGroup}>
            <Typography className={classes.sectionTitle}>
              <ColorLensIcon />
              Base Colors
            </Typography>
            <Grid container spacing={2}>
              <ColorInput
                label="Background"
                property="background"
                value={tempTheme.background}
                description="Main background color"
              />
              <ColorInput
                label="Text"
                property="text"
                value={tempTheme.text}
                description="Primary text color"
              />
              <ColorInput
                label="Card Background"
                property="cardBackground"
                value={tempTheme.cardBackground}
                description="Card and panel backgrounds"
              />
              <ColorInput
                label="Border Color"
                property="borderColor"
                value={tempTheme.borderColor}
                description="Border and divider color"
              />
              <ColorInput
                label="Hover Color"
                property="hoverColor"
                value={tempTheme.hoverColor}
                description="Hover state background"
              />
              <ColorInput
                label="Shadow Color"
                property="shadowColor"
                value={tempTheme.shadowColor}
                description="Drop shadow color"
              />
            </Grid>
          </Box>

          <Box className={classes.propertyGroup}>
            <Typography className={classes.sectionTitle}>
              <LightModeIcon />
              Theme Colors
            </Typography>
            <Grid container spacing={2}>
              <ColorInput
                label="Primary"
                property="primary"
                value={tempTheme.primary}
                description="Main brand color"
              />
              <ColorInput
                label="Secondary"
                property="secondary"
                value={tempTheme.secondary}
                description="Secondary brand color"
              />
              <ColorInput
                label="Success"
                property="success"
                value={tempTheme.success}
                description="Success states"
              />
              <ColorInput
                label="Warning"
                property="warning"
                value={tempTheme.warning}
                description="Warning states"
              />
              <ColorInput
                label="Error"
                property="error"
                value={tempTheme.error}
                description="Error states"
              />
              <ColorInput
                label="Info"
                property="info"
                value={tempTheme.info}
                description="Information states"
              />
            </Grid>
          </Box>

          <Box className={classes.propertyGroup}>
            <Typography className={classes.sectionTitle}>
              <LightModeIcon />
              Gradient Generator
            </Typography>
            <Grid container spacing={2} alignItems="center">
              <Grid item xs={12} md={3}>
                <TextField
                  label="Color 1"
                  type="color"
                  value={gradientColor1}
                  onChange={(e) => setGradientColor1(e.target.value)}
                  className={classes.colorInput}
                  variant="outlined"
                  size="small"
                />
              </Grid>
              <Grid item xs={12} md={3}>
                <TextField
                  label="Color 2"
                  type="color"
                  value={gradientColor2}
                  onChange={(e) => setGradientColor2(e.target.value)}
                  className={classes.colorInput}
                  variant="outlined"
                  size="small"
                />
              </Grid>
              <Grid item xs={12} md={3}>
                <TextField
                  label="Direction"
                  value={gradientDirection}
                  onChange={(e) => setGradientDirection(e.target.value)}
                  variant="outlined"
                  size="small"
                  fullWidth
                  placeholder="e.g., 135deg, to right"
                />
              </Grid>
              <Grid item xs={12} md={3}>
                <Button
                  variant="contained"
                  onClick={handleGradientUpdate}
                  color="primary"
                  fullWidth
                  className={classes.actionButton}
                >
                  Apply Gradient
                </Button>
              </Grid>
              <Grid item xs={12}>
                <Box
                  className={classes.gradientPreview}
                  style={{
                    background: generateGradient(
                      gradientColor1,
                      gradientColor2,
                      gradientDirection
                    ),
                  }}
                />
              </Grid>
            </Grid>
          </Box>
        </TabPanel>

        <TabPanel value={activeTab} index={2}>
          <Box className={classes.propertyGroup}>
            <Typography className={classes.sectionTitle}>
              <SettingsIcon />
              Navigation & Layout
            </Typography>
            <Grid container spacing={2}>
              <ColorInput
                label="Navigation Background"
                property="navBackground"
                value={tempTheme.navBackground}
                description="Top navigation background"
              />
              <ColorInput
                label="Sidebar Background"
                property="sidebarBackground"
                value={tempTheme.sidebarBackground}
                description="Side menu background"
              />
              <ColorInput
                label="Header Background"
                property="headerBackground"
                value={tempTheme.headerBackground}
                description="Header section background"
              />
              <ColorInput
                label="Active Color"
                property="activeColor"
                value={tempTheme.activeColor}
                description="Active state color"
              />
              <ColorInput
                label="Focus Color"
                property="focusColor"
                value={tempTheme.focusColor}
                description="Focus state color"
              />
              <TextInput
                label="Text Secondary"
                property="textSecondary"
                value={tempTheme.textSecondary}
                description="Secondary text color"
              />
            </Grid>
          </Box>

          <Box className={classes.propertyGroup}>
            <Typography className={classes.sectionTitle}>
              <ColorLensIcon />
              Component Colors
            </Typography>
            <Grid container spacing={2}>
              <ColorInput
                label="Input Background"
                property="inputBackground"
                value={tempTheme.inputBackground}
                description="Form input backgrounds"
              />
              <ColorInput
                label="Input Border"
                property="inputBorder"
                value={tempTheme.inputBorder}
                description="Form input borders"
              />
              <ColorInput
                label="Button Disabled"
                property="buttonDisabled"
                value={tempTheme.buttonDisabled}
                description="Disabled button color"
              />
              <ColorInput
                label="Link Color"
                property="linkColor"
                value={tempTheme.linkColor}
                description="Hyperlink color"
              />
            </Grid>
          </Box>
        </TabPanel>
      </DialogContent>

      <DialogActions
        style={{
          padding: "16px 24px",
          backgroundColor: theme.backgroundSecondary,
        }}
      >
        <Box
          display="flex"
          justifyContent="space-between"
          width="100%"
          alignItems="center"
        >
          <Typography variant="caption" color="textSecondary">
            Changes are applied automatically
          </Typography>
          <Box display="flex" gap={1}>
            <Button
              onClick={resetToDefault}
              color="secondary"
              className={classes.actionButton}
            >
              Reset
            </Button>
            <Button
              onClick={onClose}
              variant="contained"
              color="primary"
              className={`${classes.actionButton} ${classes.primaryActionButton}`}
            >
              Done
            </Button>
          </Box>
        </Box>
      </DialogActions>
    </Dialog>
  );
}
